// ============================================================================
// AUDIT PARTNER FORM MODAL — FSA Signature Workflow
// ============================================================================
// Create/Edit master form for fsa.tbl_audit_partner. Firm dropdown is
// async (SyutSelect isAsync) so the picker survives large firm rosters
// without preloading.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { fsaAuditPartnerService, fsaAuditFirmService } from 'api/services/fsa/fsaSignatureService';
import type { AuditPartner } from 'api/types/fsa/signature.types';

export interface AuditPartnerFormData {
  firmId: string;
  partnerName: string;
  membershipNumber: string;
  designation: string;
  signatureBlockText: string;
  defaultUdinFormat: string;
}

const INITIAL_FORM_DATA: AuditPartnerFormData = {
  firmId: '',
  partnerName: '',
  membershipNumber: '',
  designation: 'Partner',
  signatureBlockText: '',
  defaultUdinFormat: '',
};

const DESIGNATION_OPTIONS = [
  { value: 'Partner', label: 'Partner' },
  { value: 'Proprietor', label: 'Proprietor' },
  { value: 'Senior Partner', label: 'Senior Partner' },
  { value: 'Managing Partner', label: 'Managing Partner' },
];

interface AuditPartnerFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: AuditPartner | null;
  /** Optional pre-selected firm when opened from a firm's partner-roster view. */
  defaultFirmId?: string;
  onSaved: () => void;
  onClose: () => void;
}

const AuditPartnerFormModal: React.FC<AuditPartnerFormModalProps> = ({ isOpen, mode, record, defaultFirmId, onSaved, onClose }) => {
  const [formData, setFormData] = useState<AuditPartnerFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const recordRef = useRef(record);
  recordRef.current = record;
  const initialSnapshotRef = useRef('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const data: AuditPartnerFormData = recordRef.current ? {
      firmId:             String(recordRef.current.firmId),
      partnerName:        recordRef.current.partnerName,
      membershipNumber:   recordRef.current.membershipNumber,
      designation:        recordRef.current.designation,
      signatureBlockText: recordRef.current.signatureBlockText ?? '',
      defaultUdinFormat:  recordRef.current.defaultUdinFormat ?? '',
    } : {
      ...INITIAL_FORM_DATA,
      firmId: defaultFirmId ?? '',
    };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Async firm loader for SyutSelect — fetches on focus + on each search keystroke.
  const loadFirmOptions = useCallback(async (search: string) => {
    const resp = await fsaAuditFirmService.getAll({
      page: 1, pageSize: 50,
      searchTerm: search || undefined,
      includeInactive: false,
    });
    return resp.data.map(f => ({
      value: String(f.id),
      label: `${f.firmName} (${f.firmRegistrationNumber})`,
    }));
  }, []);

  // Seed option for edit mode so the dropdown shows the current firm's
  // label before the async loader fires.
  const seedOptions = useMemo(() => {
    if (record?.firmId && record?.firmName) {
      return [{ value: String(record.firmId), label: `${record.firmName} (${record.firmRegistrationNumber})` }];
    }
    return undefined;
  }, [record]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.firmId)                       errors.firmId = 'Firm is required';
    if (!formData.partnerName.trim())           errors.partnerName = 'Partner name is required';
    if (!formData.membershipNumber.trim())      errors.membershipNumber = 'Membership number is required';
    if (!formData.designation.trim())           errors.designation = 'Designation is required';
    if (formData.partnerName.length > 200)      errors.partnerName = 'Name cannot exceed 200 characters';
    if (formData.membershipNumber.length > 20)  errors.membershipNumber = 'Membership cannot exceed 20 characters';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        firmId:             formData.firmId,
        partnerName:        formData.partnerName.trim(),
        membershipNumber:   formData.membershipNumber.trim(),
        designation:        formData.designation.trim(),
        signatureBlockText: formData.signatureBlockText.trim() || null,
        defaultUdinFormat:  formData.defaultUdinFormat.trim() || null,
        statusCode:         'ACTIVE',
      };
      if (mode === 'create') {
        await fsaAuditPartnerService.create(payload);
      } else if (recordRef.current) {
        await fsaAuditPartnerService.update(recordRef.current.id, {
          ...payload,
          rowVersion: recordRef.current.rowVersion,
        });
      }
      setIsSaving(false);
      notificationService.toast.success(`"${formData.partnerName}" ${mode === 'create' ? 'created' : 'updated'} successfully.`);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Save failed', 6000);
    }
  }, [mode, formData, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? 'Add Audit Partner' : 'Edit Audit Partner'}</h2>
          <button className="modal-close" onClick={handleClose}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="firmId">
            <SyutSelect
              label="Audit Firm"
              required
              value={formData.firmId}
              onChange={(val) => { clearFieldError('firmId'); setFormData(prev => ({ ...prev, firmId: (val as string) || '' })); }}
              isAsync
              loadOptions={loadFirmOptions}
              defaultOptions
              cacheOptions={false}
              seedOptions={seedOptions}
              error={getFieldError('firmId')}
              placeholder="Select audit firm…"
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="partnerName">
              <label className="form-label">Partner Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('partnerName') ? ' form-field-error' : ''}`}
                value={formData.partnerName}
                onChange={(e) => { clearFieldError('partnerName'); setFormData(prev => ({ ...prev, partnerName: e.target.value })); }}
                maxLength={200}
              />
              {getFieldError('partnerName') && <div className="form-error-message">{getFieldError('partnerName')}</div>}
            </div>
            <div className="form-group" data-field="membershipNumber">
              <label className="form-label">Membership Number <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('membershipNumber') ? ' form-field-error' : ''}`}
                value={formData.membershipNumber}
                onChange={(e) => { clearFieldError('membershipNumber'); setFormData(prev => ({ ...prev, membershipNumber: e.target.value })); }}
                placeholder="ICAI membership"
                maxLength={20}
              />
              {getFieldError('membershipNumber') && <div className="form-error-message">{getFieldError('membershipNumber')}</div>}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="designation">
              <SyutSelect
                label="Designation"
                required
                value={formData.designation}
                onChange={(val) => { clearFieldError('designation'); setFormData(prev => ({ ...prev, designation: (val as string) || 'Partner' })); }}
                options={DESIGNATION_OPTIONS}
                isClearable={false}
                error={getFieldError('designation')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Default UDIN Format</label>
              <input
                type="text"
                className="form-input"
                value={formData.defaultUdinFormat}
                onChange={(e) => setFormData(prev => ({ ...prev, defaultUdinFormat: e.target.value }))}
                placeholder="Optional 18-char UDIN starter"
                maxLength={50}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Signature Block Text</label>
            <input
              type="text"
              className="form-input"
              value={formData.signatureBlockText}
              onChange={(e) => setFormData(prev => ({ ...prev, signatureBlockText: e.target.value }))}
              maxLength={500}
              placeholder="Optional per-partner line printed below their name"
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`${mode === 'create' ? 'Create' : 'Update'} audit partner "${formData.partnerName}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default AuditPartnerFormModal;
